/**
 * Klaviyo email capture
 *
 * Fires a server-side "email captured" event the moment a user enters a valid
 * email in any form on the site (on blur / change), before the form is completed,
 * so we do not lose people who abandon. The API key stays on the backend; the
 * browser only posts the email + source to a nonce-protected AJAX endpoint.
 *
 * One user action = one event: identical email+source pairs are sent at most once
 * per page (client-side), and the backend de-duplicates again within a time window.
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const KlaviyoCapture = {
    sent: new Set(),

    init() {
        if (typeof pe_mp_klaviyo === 'undefined' || !pe_mp_klaviyo.ajax_url) {
            return;
        }

        // Delegated listeners so dynamically injected inputs (popups, quiz steps,
        // gated-file forms) are covered without re-binding.
        document.addEventListener('focusout', (e) => this.handle(e.target), true);
        document.addEventListener('change', (e) => this.handle(e.target), true);
    },

    handle(field) {
        if (!field || field.tagName !== 'INPUT') {
            return;
        }
        if (field.type !== 'email' && (field.getAttribute('inputmode') !== 'email')) {
            return;
        }
        if (field.readOnly || field.disabled) {
            return;
        }
        // Join and reserve reach Klaviyo only after the server bot checks.
        if (field.closest('#join-psystandard-form, #reserve-form')) {
            return;
        }

        const email = (field.value || '').trim();
        if (!EMAIL_REGEX.test(email)) {
            return;
        }

        const source = this.resolveSource(field);
        const key = `${email.toLowerCase()}|${source}`;
        if (this.sent.has(key)) {
            return;
        }
        this.sent.add(key);

        this.send(email, source);
    },

    /**
     * Determine which form the email came from (quiz | waitlist | webinar | ...).
     */
    resolveSource(field) {
        const explicit = field.closest('[data-klaviyo-source]');
        if (explicit) {
            return explicit.getAttribute('data-klaviyo-source');
        }

        const form = field.closest('form');
        if (form) {
            const type = form.getAttribute('data-form-type');
            if (type) {
                return type;
            }
            if (form.id) {
                return form.id.replace(/-form$/, '');
            }
        }

        if (field.name === 'pat_email') {
            return 'pat-quiz';
        }

        return 'unknown';
    },

    send(email, source) {
        const body = new FormData();
        body.append('action', 'pe_mp_klaviyo_capture_email');
        body.append('nonce', pe_mp_klaviyo.nonce);
        body.append('email', email);
        body.append('source', source);

        try {
            fetch(pe_mp_klaviyo.ajax_url, {
                method: 'POST',
                body,
                credentials: 'same-origin',
                keepalive: true,
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            }).catch(() => {});
        } catch (e) {
            // Never let lead capture interfere with the user's flow.
        }
    },
};

export default KlaviyoCapture;
